// src/features/people/PersonForm.jsx - CORREGIDO: Campos completos + Manejo de errores
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Form, Button, Container, Row, Col, Card, Spinner } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { FaSave, FaTimes } from 'react-icons/fa';
import PeopleService from './PeopleService';
import CustomSnackbar from '../../components/CustomSnackbar';
import './PersonForm.css';
import dayjs from 'dayjs';

function PersonForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, type: '', message: '' });
  
  // ✅ useRef para control total de requests
  const loadingRef = useRef(false);
  const loadedPersonRef = useRef(null);
  const mountedRef = useRef(true);
  
  const [formData, setFormData] = useState({
    type: 'F', // ✅
    firstName: '',
    surname: '',
    secondName: '', // ✅ NUEVO - Segundo nombre
    secondSurname: '', // ✅ NUEVO - Segundo apellido
    businessName: '',
    documentType: 'CI',
    document: '',
    phone: '',
    email: '',
    address: '',
    regime: 'Empleado',
    country: 'UY',
    gender: '', // ✅ NUEVO - Género
    civilStatus: '', // ✅ NUEVO - Estado civil
    bornDate: '', // ✅ NUEVO - Fecha de nacimiento
    entryDate: dayjs().format('YYYY-MM-DD') // ✅ Fecha por defecto siempre válida
  });

  // ✅ Función CORREGIDA para extraer mensaje de error específico del API
  const extractErrorMessage = (error) => {
    try {
      // Caso 1: Error directo del customFetch/API
      if (error.message && typeof error.message === 'string') {
        // Intentar extraer JSON del mensaje si contiene información estructurada
        if (error.message.includes('{') && error.message.includes('}')) {
          try {
            const jsonMatch = error.message.match(/\{.*\}/);
            if (jsonMatch) {
              const errorObj = JSON.parse(jsonMatch[0]);
              
              if (errorObj.message) {
                if (Array.isArray(errorObj.message)) {
                  return errorObj.message.join(', ');
                }
                return errorObj.message;
              }
            }
          } catch (parseError) {
            // Ignorar error de parsing
          }
        }
        
        // Si no es JSON, usar el mensaje tal como viene
        return error.message;
      }
      
      // Caso 2: Error con respuesta de fetch
      if (error.response && error.response.data) {
        const errorData = error.response.data;
        
        if (errorData.message) {
          if (Array.isArray(errorData.message)) {
            return errorData.message.join(', ');
          }
          return errorData.message;
        }
      }
      
      // Fallback a mensaje genérico
      return `Error al ${id ? 'actualizar' : 'crear'} la persona`;
    } catch (extractError) {
      console.error('❌ Error al extraer mensaje:', extractError);
      return `Error al ${id ? 'actualizar' : 'crear'} la persona`;
    }
  };

  // ✅ Función mejorada para formatear fecha de manera segura
  const formatDateSafely = (dateValue) => {
    if (!dateValue) {
      return '';
    }
    
    try {
      const parsed = dayjs(dateValue);
      if (!parsed.isValid()) {
        return '';
      }
      return parsed.format('YYYY-MM-DD');
    } catch (error) {
      console.error(`Error al formatear fecha ${dateValue}:`, error);
      return '';
    }
  };

  // ✅ Función estable para cargar SOLO datos básicos de persona
  const loadPersonForEdit = useCallback(async (personId) => {
    // ✅ Verificar si el componente sigue montado
    if (!mountedRef.current) {
      return;
    }

    // ✅ Prevenir múltiples llamadas simultáneas
    if (loadingRef.current) {
      return;
    }

    // ✅ Verificar si ya tenemos los datos de esta persona
    if (loadedPersonRef.current === personId) {
      return;
    }

    try {
      loadingRef.current = true;
      setLoading(true);
      
      // ✅ USAR MÉTODO OPTIMIZADO - Solo 1 request con datos básicos
      const person = await PeopleService.getPersonForEdit(personId);

      // ✅ Verificar si el componente sigue montado
      if (!mountedRef.current) {
        return;
      }

      // ✅ Marcar que ya cargamos esta persona
      loadedPersonRef.current = personId;

      // ✅ CORREGIDO: Formateo seguro de fechas + preservar valores existentes
      const formattedEntryDate = person.entryDate ? formatDateSafely(person.entryDate) : dayjs().format('YYYY-MM-DD');
      const formattedBornDate = person.bornDate ? formatDateSafely(person.bornDate) : '';

      // ✅ CORREGIDO: Preservar valores originales y manejar campos vacíos correctamente
      setFormData(prevData => ({
        ...person,
        // ✅ Fechas formateadas
        entryDate: formattedEntryDate,
        bornDate: formattedBornDate,
        // ✅ Campos de texto - permitir vacíos
        firstName: person.firstName || '',
        surname: person.surname || '',
        secondName: person.secondName || '', // ✅ NUEVO
        secondSurname: person.secondSurname || '', // ✅ NUEVO
        businessName: person.businessName || '',
        phone: person.phone !== null && person.phone !== undefined ? String(person.phone) : '',
        email: person.email || '',
        address: person.address || '',
        gender: person.gender || '', // ✅ NUEVO
        civilStatus: person.civilStatus || '' // ✅ NUEVO
      }));
      
    } catch (error) {
      console.error(`❌ Error al cargar datos para edición de persona ${personId}:`, error);
      
      if (mountedRef.current) {
        setSnackbar({
          open: true,
          type: 'error',
          message: 'Error al cargar los datos de la persona'
        });
        
        // ✅ Resetear ref en caso de error
        loadedPersonRef.current = null;
      }
    } finally {
      loadingRef.current = false;
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, []); // ✅ Sin dependencias - función completamente estable

  // ✅ useEffect que solo ejecuta UNA VEZ por ID
  useEffect(() => {
    if (id) {
      loadPersonForEdit(id);
    } else {
      // Resetear refs para modo creación
      loadedPersonRef.current = null;
    }

    // ✅ Cleanup al desmontar
    return () => {
      mountedRef.current = false;
      loadingRef.current = false;
    };
  }, [id]); // ✅ Solo depende del ID del parámetro

  // ✅ useEffect separado para marcar componente como montado
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // ✅ Validación especial para fechas
    if (name === 'entryDate') {
      // Verificar que la fecha sea válida antes de actualizar
      if (value && dayjs(value).isValid()) {
        setFormData(prev => ({
          ...prev,
          [name]: value
        }));
      } else if (!value) {
        // Si se borra la fecha, usar fecha actual como fallback
        setFormData(prev => ({
          ...prev,
          [name]: dayjs().format('YYYY-MM-DD')
        }));
      }
    } else if (name === 'bornDate') {
      // Para fecha de nacimiento, permitir vacío
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const validateForm = () => {
    if (!formData.document?.trim()) {
      setSnackbar({
        open: true,
        type: 'error',
        message: 'El número de documento es requerido'
      });
      return false;
    }

    if (formData.type === 'F') {
      if (!formData.firstName?.trim()) {
        setSnackbar({
          open: true,
          type: 'error',
          message: 'El nombre es requerido'
        });
        return false;
      }
      if (!formData.surname?.trim()) {
        setSnackbar({
          open: true,
          type: 'error',
          message: 'El apellido es requerido'
        });
        return false;
      }
    } else {
      if (!formData.businessName?.trim()) {
        setSnackbar({
          open: true,
          type: 'error',
          message: 'La razón social es requerida'
        });
        return false;
      }
    }

    // ✅ Validación mejorada de fecha de ingreso (obligatoria)
    if (!formData.entryDate || !dayjs(formData.entryDate).isValid()) {
      setSnackbar({
        open: true,
        type: 'error',
        message: 'La fecha de ingreso es requerida y debe ser válida'
      });
      return false;
    }

    // ✅ Validación de fecha de nacimiento (opcional, pero si se ingresa debe ser válida)
    if (formData.bornDate && formData.bornDate.trim().length > 0) {
      if (!dayjs(formData.bornDate).isValid()) {
        setSnackbar({
          open: true,
          type: 'error',
          message: 'La fecha de nacimiento debe ser válida'
        });
        return false;
      }
    }

    // ✅ CORREGIDO: Validación de email solo si hay contenido
    if (formData.email && formData.email.trim().length > 0) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email.trim())) {
        setSnackbar({
          open: true,
          type: 'error',
          message: 'El formato del email no es válido'
        });
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // ✅ BLOQUEO INMEDIATO - Esta es la clave
    if (loading) {
      return;
    }

    if (!validateForm()) {
      return;
    }
    
    try {
      setLoading(true);
      
      // ✅ CORREGIDO: Limpiar campos vacíos antes de enviar
      const dataToSend = { ...formData };
      
      // Solo enviar campos si realmente tienen contenido
      if (!dataToSend.email || dataToSend.email.trim().length === 0) {
        dataToSend.email = '';
      }
      
      if (!dataToSend.phone || dataToSend.phone.trim().length === 0) {
        dataToSend.phone = '';
      }

      if (!dataToSend.address || dataToSend.address.trim().length === 0) {
        dataToSend.address = '';
      }

      // ✅ NUEVO: Limpiar campos nuevos si están vacíos
      if (!dataToSend.secondName || dataToSend.secondName.trim().length === 0) {
        dataToSend.secondName = '';
      }

      if (!dataToSend.secondSurname || dataToSend.secondSurname.trim().length === 0) {
        dataToSend.secondSurname = '';
      }

      if (!dataToSend.gender || dataToSend.gender.trim().length === 0) {
        dataToSend.gender = '';
      }

      if (!dataToSend.civilStatus || dataToSend.civilStatus.trim().length === 0) {
        dataToSend.civilStatus = '';
      }

      if (!dataToSend.bornDate || dataToSend.bornDate.trim().length === 0) {
        dataToSend.bornDate = '';
      }
      
      if (id) {
        await PeopleService.updatePerson(id, dataToSend);
        setSnackbar({
          open: true,
          type: 'success',
          message: 'Persona actualizada exitosamente'
        });
      } else {
        const result = await PeopleService.createPerson(dataToSend);
        setSnackbar({
          open: true,
          type: 'success',
          message: 'Persona creada exitosamente'
        });
      }
      
      setTimeout(() => {
        navigate('/people');
      }, 1500);
      
    } catch (error) {
      console.error('❌ Error saving person:', error);
      
      // ✅ CORREGIDO: Extraer mensaje específico del error del API
      const specificErrorMessage = extractErrorMessage(error);
      
      setSnackbar({
        open: true,
        type: 'error',
        message: specificErrorMessage
      });
      
      // ✅ IMPORTANTE: Solo resetear loading, NO tocar formData para preservar los datos
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Container className="person-form-container">
      <Card className="form-card">
        <Card.Header className="bg-primary text-white text-center">
          <h5 className="m-0">
            {id ? 'Editar' : 'Agregar nueva'} Persona
          </h5>
        </Card.Header>
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group className="mb-2">
                  <Form.Label>Tipo de Persona</Form.Label>
                  <Form.Select
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    disabled={id || loading}
                  >
                    <option value="F">Persona Física</option>
                    <option value="J">Persona Jurídica</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              
              <Col md={6}>
                <Form.Group className="mb-2">
                  <Form.Label className="required-field">Régimen</Form.Label>
                  <Form.Select
                    name="regime"
                    value={formData.regime}
                    onChange={handleChange}
                    disabled={loading}
                    required
                  >
                    <option value="Empleado">Empleado</option>
                    <option value="Cliente">Cliente</option>
                    <option value="Subcontrato">Subcontratado</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            
            {formData.type === 'F' ? (
              <>
                {/* ✅ NOMBRES - Fila 1 */}
                <Row className="mb-2">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="required-field">Nombre</Form.Label>
                      <Form.Control
                        type="text"
                        name="firstName"
                        value={formData.firstName || ''}
                        onChange={handleChange}
                        disabled={loading}
                        required
                      />
                    </Form.Group>
                  </Col>
                  
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Segundo Nombre</Form.Label>
                      <Form.Control
                        type="text"
                        name="secondName"
                        value={formData.secondName || ''}
                        onChange={handleChange}
                        disabled={loading}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                {/* ✅ APELLIDOS - Fila 2 */}
                <Row className="mb-2">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="required-field">Apellido</Form.Label>
                      <Form.Control
                        type="text"
                        name="surname"
                        value={formData.surname || ''}
                        onChange={handleChange}
                        disabled={loading}
                        required
                      />
                    </Form.Group>
                  </Col>
                  
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Segundo Apellido</Form.Label>
                      <Form.Control
                        type="text"
                        name="secondSurname"
                        value={formData.secondSurname || ''}
                        onChange={handleChange}
                        disabled={loading}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                {/* ✅ DATOS PERSONALES - Fila 3 */}
                <Row className="mb-2">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Género</Form.Label>
                      <Form.Select
                        name="gender"
                        value={formData.gender || ''}
                        onChange={handleChange}
                        disabled={loading}
                      >
                        <option value="">Seleccionar...</option>
                        <option value="M">Masculino</option>
                        <option value="F">Femenino</option>
                        <option value="O">Otro</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Estado Civil</Form.Label>
                      <Form.Select
                        name="civilStatus"
                        value={formData.civilStatus || ''}
                        onChange={handleChange}
                        disabled={loading}
                      >
                        <option value="">Seleccionar...</option>
                        <option value="Soltero">Soltero/a</option>
                        <option value="Casado">Casado/a</option>
                        <option value="Divorciado">Divorciado/a</option>
                        <option value="Viudo">Viudo/a</option>
                        <option value="Union Libre">Unión Libre</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Fecha de Nacimiento</Form.Label>
                      <Form.Control
                        type="date"
                        name="bornDate"
                        value={formData.bornDate || ''}
                        onChange={handleChange}
                        disabled={loading}
                        min="1900-01-01"
                        max={dayjs().format('YYYY-MM-DD')}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </>
            ) : (
              <Row className="mb-2">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label className="required-field">Razón Social</Form.Label>
                    <Form.Control
                      type="text"
                      name="businessName"
                      value={formData.businessName || ''}
                      onChange={handleChange}
                      disabled={loading}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>
            )}
            
            {/* ✅ DOCUMENTOS - Fila 4 */}
            <Row className="mb-2">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Tipo Documento</Form.Label>
                  <Form.Select
                    name="documentType"
                    value={formData.documentType}
                    onChange={handleChange}
                    disabled={loading}
                  >
                    {formData.type === 'F' ? (
                      <>
                        <option value="CI">CI</option>
                        <option value="PASAPORTE">Pasaporte</option>
                      </>
                    ) : (
                      <>
                        <option value="RUT">RUT</option>
                      </>
                    )}
                  </Form.Select>
                </Form.Group>
              </Col>
              
              <Col md={4}>
                <Form.Group>
                  <Form.Label>País</Form.Label>
                  <Form.Select
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    disabled={loading}
                  >
                    <option value="UY">Uruguay</option>
                    <option value="O">Otro</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="required-field">Documento</Form.Label>
                  <Form.Control
                    type="text"
                    name="document"
                    value={formData.document || ''}
                    onChange={handleChange}
                    disabled={loading}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            
            {/* ✅ CONTACTO - Fila 5 */}
            <Row className="mb-2">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Teléfono</Form.Label>
                  <Form.Control
                    type="tel"
                    name="phone"
                    value={formData.phone || ''}
                    onChange={handleChange}
                    disabled={loading}
                  />
                </Form.Group>
              </Col>
              
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email || ''}
                    onChange={handleChange}
                    disabled={loading}
                    // ✅ CORREGIDO: Validación visual solo si hay contenido
                    isInvalid={formData.email && formData.email.trim().length > 0 && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())}
                  />
                  <Form.Control.Feedback type="invalid">
                    El formato del email no es válido
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
            
            {/* ✅ DIRECCIÓN Y FECHAS - Fila 6 */}
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Dirección</Form.Label>
                  <Form.Control
                    type="text"
                    name="address"
                    value={formData.address || ''}
                    onChange={handleChange}
                    disabled={loading}
                  />
                </Form.Group>
              </Col>
              
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="required-field">Fecha de Ingreso</Form.Label>
                  <Form.Control
                    type="date"
                    name="entryDate"
                    value={formData.entryDate || ''}
                    onChange={handleChange}
                    disabled={loading}
                    required
                    // ✅ Agregar min/max para evitar fechas absurdas
                    min="1900-01-01"
                    max={dayjs().add(1, 'year').format('YYYY-MM-DD')}
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <div className="form-actions">
              <Button 
                variant="secondary" 
                onClick={() => navigate('/people')}
                className="cancel-button"
                disabled={loading}
                type="button"
              >
                <FaTimes className="me-1" /> Cancelar
              </Button>
              
              <Button 
                variant="primary" 
                type="submit"
                className="submit-button"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-1"
                    />
                    Guardando...
                  </>
                ) : (
                  <>
                    <FaSave className="me-1" /> {id ? 'Actualizar' : 'Crear'}
                  </>
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
      
      <CustomSnackbar
        open={snackbar.open}
        type={snackbar.type}
        message={snackbar.message}
        onClose={handleCloseSnackbar}
      />
    </Container>
  );
}

export default PersonForm;